import React, { useState } from 'react';
import { geoEquirectangular, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import worldTopo from 'world-atlas/countries-110m.json';
import { Plus, Minus, X, ArrowUpRight } from 'lucide-react';
import { globalSection as gs } from '../../mock';

const W = 1000;
const H = 500;
const world = feature(worldTopo, worldTopo.objects.countries);
const projection = geoEquirectangular().fitSize([W, H], world);
const pathGen = geoPath(projection);
const countryPaths = world.features.map((f) => pathGen(f));
const portPoints = gs.ports.map((p) => {
  const [x, y] = projection([p.lng, p.lat]);
  return { ...p, x, y };
});

const mapsUrl = (p) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${p.name} port ${p.country}`)}`;

const Global = () => {
  const [zoom, setZoom] = useState(1);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);

  const s = 1 / zoom; // keep markers/labels constant apparent size when zoomed
  const vbW = W / zoom;
  const vbH = H / zoom;
  const vbX = (W - vbW) / 2;
  const vbY = (H - vbH) / 2;

  return (
    <div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.55fr)] gap-8 items-end mb-10">
        <div>
          <div className="label-mono text-ember mb-6">[{gs.index}] · {gs.label}</div>
          <h2 className="font-display font-light text-white text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
            {gs.headingLead}
            <span className="text-white/45">{gs.headingMuted}</span>
          </h2>
        </div>
        <p className="text-white/65 font-body text-base leading-relaxed">{gs.sub}</p>
      </div>

      {/* Map */}
      <div className="relative w-full aspect-[2/1] border border-white/10 overflow-hidden bg-[#0a0e12] rounded-lg">
        <svg viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`} className="w-full h-full" style={{ transition: 'all 0.4s ease' }}>
          {/* Countries */}
          <g>
            {countryPaths.map((d, i) => (
              <path key={i} d={d} fill="#15181d" stroke="#282d34" strokeWidth={0.5 * s} />
            ))}
          </g>

          {/* Port markers */}
          {portPoints.map((p) => {
            const active = hovered === p.name || p.hq;
            const labelW = p.name.length * 6.1 * s + 14 * s;
            return (
              <g
                key={p.name}
                className="cursor-pointer"
                onMouseEnter={() => setHovered(p.name)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => setSelected(p)}
              >
                {p.hq && <circle cx={p.x} cy={p.y} r={9 * s} fill="rgba(239,123,36,0.35)" className="animate-ping" style={{ transformOrigin: `${p.x}px ${p.y}px` }} />}
                <circle cx={p.x} cy={p.y} r={(p.hq ? 4.5 : 3.5) * s} fill="#EF7B24" stroke="#fff" strokeWidth={0.6 * s} />
                {active && (
                  <g>
                    <rect x={p.x + 7 * s} y={p.y - 8 * s} width={labelW} height={16 * s} rx={2 * s} fill="rgba(8,16,22,0.9)" stroke="rgba(255,255,255,0.18)" strokeWidth={0.5 * s} />
                    <text x={p.x + 13 * s} y={p.y + 3.5 * s} fill="#fff" fontSize={9 * s} letterSpacing={0.5 * s} style={{ fontFamily: "'Space Mono', monospace", textTransform: 'uppercase' }}>
                      {p.name.toUpperCase()}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>

        {/* Zoom controls */}
        <div className="absolute top-4 left-4 flex flex-col border border-white/15 bg-navy/60 backdrop-blur z-10 rounded">
          <button onClick={() => setZoom((z) => Math.min(3, +(z + 0.4).toFixed(2)))} className="p-2 text-white/80 hover:text-ember border-b border-white/10" aria-label="Zoom in"><Plus size={16} /></button>
          <button onClick={() => setZoom((z) => Math.max(1, +(z - 0.4).toFixed(2)))} className="p-2 text-white/80 hover:text-ember" aria-label="Zoom out"><Minus size={16} /></button>
        </div>
        <button
          onClick={() => setZoom(1)}
          className="absolute top-4 right-4 label-mono text-white/70 hover:text-ember border border-white/15 bg-navy/60 backdrop-blur px-3 py-2 z-10 rounded"
        >
          Reset view
        </button>
      </div>

      {/* Port list */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {gs.ports.map((p) => (
          <button
            key={p.name}
            onMouseEnter={() => setHovered(p.name)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => setSelected(p)}
            className={`flex items-center justify-between gap-2 border px-3.5 py-3 rounded font-body text-sm text-left transition-colors ${
              hovered === p.name ? 'border-ember/60 bg-white/[0.05]' : 'border-white/10 bg-white/[0.02]'
            }`}
          >
            <span className="flex items-center gap-2.5 text-white/85 truncate">
              <span className="text-base leading-none">{p.flag}</span>
              <span className="truncate">{p.name}</span>
            </span>
            {p.hq && <span className="label-mono text-ember shrink-0">HQ</span>}
          </button>
        ))}
      </div>

      {/* Detail popup */}
      {selected && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="port-modal"
        >
          <div className="absolute inset-0 bg-navy-deep/80 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
            style={{ background: 'linear-gradient(180deg, #0d2830 0%, #081e26 100%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelected(null)}
              className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-navy/60 border border-white/15 text-white/80 hover:text-ember hover:border-ember/50 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <div className="relative aspect-[16/10] overflow-hidden">
              <img src={selected.img} alt={selected.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2830] via-transparent to-transparent" />
            </div>
            <div className="p-7">
              <div className="flex items-center gap-2.5 label-mono text-white/70">
                <span className="text-base leading-none">{selected.flag}</span>
                {selected.country}
                {selected.hq && <span className="text-ember">· HQ</span>}
              </div>
              <h3 className="mt-3 font-display font-medium text-white text-3xl">{selected.name}</h3>
              <div className="mt-3 w-10 h-0.5 bg-ember" />
              <p className="mt-4 text-white/70 font-body text-[15px] leading-relaxed">{selected.desc}</p>
              <a
                href={mapsUrl(selected)}
                target="_blank"
                rel="noreferrer"
                className="group mt-6 inline-flex items-center gap-2.5 border border-white/15 bg-white/[0.04] hover:border-ember/50 hover:bg-white/[0.08] px-5 py-3 rounded label-mono text-ember transition-colors"
              >
                View on Google Maps
                <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Global;
